import { ApiError } from "@/common/lib/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Copy, MailQuestion, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { type ReceivedInvoiceRow, useDismissReceivedInvoice } from "../api/receivedInvoicesApi";
import { formatInvoiceAmount } from "../utils/formatInvoiceAmount";
import {
  extractGmailVerificationCode,
  isForwardingVerificationCandidate,
  quarantineReasonText,
} from "../utils/quarantine";
import { DocumentTypePill, PipelineStatusPill } from "./InvoicePills";

type Props = {
  invoices: ReceivedInvoiceRow[];
  accountId: string;
  // Solo owner/admin pueden descartar (el backend responde 403/404 a los
  // demás roles) — ocultar el botón es UX, la seguridad vive en el backend.
  canDismiss: boolean;
};

// Bandeja mobile-first (375px): cards apiladas, no tabla ancha. En desktop las
// cards ganan una fila horizontal pero siguen siendo cards (§9 mobile).
export function ReceivedInvoicesList({ invoices, accountId, canDismiss }: Props) {
  // Un solo dialog para toda la lista (patrón confirmDeleteId de SplitBuilder):
  // el id del correo pendiente de confirmación, null = cerrado.
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const dismissMutation = useDismissReceivedInvoice(accountId);

  async function handleDismissConfirmed() {
    if (confirmId === null || dismissMutation.isPending) return;
    try {
      await dismissMutation.mutateAsync(confirmId);
      toast.success("Correo descartado — lo encontrás con el filtro Descartados.");
      setConfirmId(null);
    } catch (e) {
      setConfirmId(null);
      toast.error(e instanceof ApiError ? e.message : "No se pudo descartar el correo.");
    }
  }

  // Botón visible solo en cuarentena: una factura real jamás se descarta
  // (para eso existe des-asignar) y las filas ya dismissed tampoco lo llevan.
  function dismissRequester(invoice: ReceivedInvoiceRow): (() => void) | null {
    if (!canDismiss || invoice.pipelineStatus !== "quarantined") return null;
    return () => setConfirmId(invoice.id);
  }

  return (
    <>
      <ul className="flex flex-col gap-2">
        {invoices.map((invoice) => (
          <li key={invoice.id}>
            {isForwardingVerificationCandidate(invoice) ? (
              <VerificationEmailCard
                invoice={invoice}
                onRequestDismiss={dismissRequester(invoice)}
              />
            ) : (
              <InvoiceCard invoice={invoice} onRequestDismiss={dismissRequester(invoice)} />
            )}
          </li>
        ))}
      </ul>

      <AlertDialog
        open={confirmId !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Descartar este correo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se ocultará de la bandeja. El correo original queda guardado y podés verlo con el
              filtro Descartados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={dismissMutation.isPending}
              className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={dismissMutation.isPending}
              // preventDefault: Radix cierra el dialog al click por defecto —
              // lo mantenemos abierto (con el botón en pending) hasta que la
              // mutación resuelva.
              onClick={(e) => {
                e.preventDefault();
                void handleDismissConfirmed();
              }}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-rose-600 px-4 font-medium text-[14px] text-white transition-colors hover:bg-rose-700 disabled:opacity-50"
            >
              {dismissMutation.isPending ? "Descartando…" : "Descartar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return format(d, "d MMM yyyy", { locale: es });
}

// Botón discreto de descartar (outline, hover rojo) — mismo lenguaje visual
// que el botón de eliminar asignación de SplitBuilder.
function DismissButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-app-border bg-app-surface px-2.5 font-medium text-[12px] text-app-ink-3 transition-colors hover:bg-rose-50 hover:text-rose-600"
    >
      <X size={14} aria-hidden="true" />
      Descartar
    </button>
  );
}

function InvoiceCard({
  invoice,
  onRequestDismiss,
}: {
  invoice: ReceivedInvoiceRow;
  onRequestDismiss: (() => void) | null;
}) {
  const quarantined = invoice.pipelineStatus === "quarantined";

  return (
    <div className="overflow-hidden rounded-2xl border border-app-border bg-app-surface">
      <Link
        to={`/app/recibidos/${invoice.id}`}
        className="block p-4 transition-colors hover:bg-app-surface-2"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="truncate font-medium text-[14px] text-app-ink-1">
                {invoice.emisorName ?? "Emisor desconocido"}
              </span>
              <DocumentTypePill type={invoice.documentType} />
            </div>
            <div className="mt-0.5 text-[12px] text-app-ink-3">
              {formatDate(invoice.issuedAt ?? invoice.receivedAt)}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span className="font-semibold text-[15px] text-app-ink-1 tabular-nums">
              {formatInvoiceAmount(invoice.totalCents, invoice.currency)}
            </span>
            <PipelineStatusPill status={invoice.pipelineStatus} />
          </div>
        </div>

        {quarantined && (
          <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
            {quarantineReasonText(invoice.quarantineReason)}
          </p>
        )}
      </Link>

      {/* Fuera del Link: un botón anidado en un anchor es HTML inválido y
          navegaría al detalle al clickearlo. */}
      {onRequestDismiss && (
        <div className="flex justify-end border-app-border border-t px-4 py-2">
          <DismissButton onClick={onRequestDismiss} />
        </div>
      )}
    </div>
  );
}

// Correo sin XML — casi siempre la verificación del reenvío automático de
// Gmail. Destacado y auto-explicado: sin esto el usuario no sabe que el correo
// del código de confirmación ya llegó (spec §10.4). Desde la parte 1.5 el
// asunto y el cuerpo (truncado) vienen en la fila; filas anteriores solo
// tienen el crudo en R2 → fallback a soporte.
function VerificationEmailCard({
  invoice,
  onRequestDismiss,
}: {
  invoice: ReceivedInvoiceRow;
  onRequestDismiss: (() => void) | null;
}) {
  // `?? null` normaliza respuestas viejas cacheadas donde el campo ni existe.
  const subject = invoice.emailSubject ?? null;
  const text = invoice.emailText ?? null;
  const code = extractGmailVerificationCode(invoice);
  const hasContent = subject !== null || text !== null;

  async function handleCopyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Código copiado.");
    } catch {
      toast.error("No se pudo copiar — seleccioná el código y copialo a mano.");
    }
  }

  return (
    <div className="rounded-2xl border border-app-emerald/40 bg-app-emerald-bg/40 p-4">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-app-emerald-bg text-app-emerald">
          <MailQuestion size={18} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-[14px] text-app-ink-1">
              Correo sin factura adjunta
            </span>
            <span className="text-[12px] text-app-ink-3">{formatDate(invoice.receivedAt)}</span>
          </div>

          {subject !== null && (
            <p className="mt-1 break-words font-medium text-[13px] text-app-ink-1">{subject}</p>
          )}

          {code !== null && (
            <div className="mt-2 flex flex-col gap-1.5">
              <span className="text-[13px] text-app-ink-2">
                Código de verificación de Gmail — pegalo en Gmail para completar el reenvío
                automático:
              </span>
              <div className="flex items-center gap-2">
                <code className="min-w-0 rounded-lg border border-app-border bg-app-surface px-3 py-2 font-mono font-semibold text-[15px] text-app-ink-1 tracking-wider">
                  {code}
                </code>
                <button
                  type="button"
                  onClick={() => void handleCopyCode()}
                  className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl border border-app-border bg-app-surface px-3 font-medium text-[13px] text-app-ink-1 transition-colors hover:bg-app-surface-2"
                >
                  <Copy size={14} aria-hidden="true" />
                  Copiar
                </button>
              </div>
            </div>
          )}

          {code === null && hasContent && (
            <p className="mt-1 text-[13px] text-app-ink-2">
              Si estás configurando el reenvío automático, el código de confirmación viene en el
              texto de abajo.
            </p>
          )}

          {text !== null && (
            <pre className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-app-surface-2 px-3 py-2 font-mono text-[12px] text-app-ink-2">
              {text}
            </pre>
          )}

          {!hasContent && (
            <p className="mt-1 text-[13px] text-app-ink-2">
              Si estás configurando el reenvío automático, este suele ser el correo de verificación
              de Gmail con el código de confirmación. Este correo llegó antes de que la app pudiera
              mostrar su contenido — contactá al soporte de Mihato para obtener el código y
              completar el reenvío.
            </p>
          )}

          {onRequestDismiss && (
            <div className="mt-3 flex justify-end">
              <DismissButton onClick={onRequestDismiss} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
